import React, { useState } from 'react';

import styled from 'styled-components';

import SplitButton from 'components/lib/SplitButton';

import ShopDataModalContractsModal from './ShopDataModalContractsModal';
import { useContractContext } from '../../../common/Contract/ContractContext';
import AddIcon from '../../../common/Icons/AddIcon';
import { COMMUNITY_CONTRACT_TYPE } from '../../../constants';
import { CommunityContract } from '../../../types/contracts';
import Button from '../../lib/Button';
import { useShopDataModalContext } from '../ShopDataModalContext';

const Container = styled.div`
  display: flex;
  gap: 15px;
  padding: 8px 30px 15px 30px;
  align-items: center;
`;

const Label = styled.div`
  font-family: var(--font-bold);
`;

const Contracts = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StyledButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: space-around;
  border: none;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  border-radius: 14px;
  width: 26px;
  height: 26px;
  padding: 0;
`;

interface Props {
  onSaveNewContractTabs: (selection: string[]) => void;
  onEditContractTab: (selection: string[]) => void;
}

const ShopDataModalContracts = ({ onSaveNewContractTabs, onEditContractTab }: Props) => {
  const { openModal, keys, selectedKey, setOpenModal, setSelectedKey } = useContractContext();
  const { community } = useShopDataModalContext();
  const [contractSlugsToEdit, setContractSlugsToEdit] = useState<string[]>([]);
  const [contractOptions, setContractOptions] = useState<CommunityContract[]>([]);

  const editContractsTab = (slugToEdit: string) => {
    setOpenModal(true);
    setContractSlugsToEdit(slugToEdit.split('|') || []);
    const unusedContracts = community?.contracts.filter(
      (contract) =>
        !keys.some((contractGroupSlug) => contractGroupSlug.split('|').some((slug) => contract.slug === slug)) ||
        selectedKey?.split('|').some((slug) => contract.slug === slug)
    );
    setContractOptions(unusedContracts || []);
  };

  const addNewContracts = () => {
    setOpenModal(true);
    setContractSlugsToEdit([]);
    setContractOptions([]);
  };

  const isEditingContractsGroup = contractOptions.length;
  const showAddMoreContractsButton = keys.flatMap((key) => key.split('|')).length < (community?.contracts.length || 0);

  return (
    <Container>
      <ShopDataModalContractsModal
        open={openModal}
        initialSelection={contractSlugsToEdit}
        contractOptions={isEditingContractsGroup ? contractOptions : community?.contracts || []}
        onConfirm={isEditingContractsGroup ? onEditContractTab : onSaveNewContractTabs}
        onClose={() => {
          setOpenModal(false);
        }}
      />
      {Boolean(keys.length) && (
        <>
          <Label>Contract Types</Label>
          <Contracts>
            {keys
              .slice()
              .sort()
              .map((item: string) => {
                const isSelected = item === selectedKey;
                const buttonType = isSelected ? 'primary' : 'default';
                const itemContractKeys = item.split('|');
                const itemContracts = itemContractKeys.map((item: string) =>
                  community?.contracts.find((c: CommunityContract) => item === c.slug)
                );

                return (
                  <SplitButton
                    key={item}
                    onContentClick={() => setSelectedKey(item)}
                    onIconClick={() => editContractsTab(item)}
                    type={buttonType}
                    content={itemContracts
                      .map((contract?: CommunityContract) => {
                        const name = contract?.name.replace('Contract Type', '').trim() ?? '';
                        if (contract?.type === COMMUNITY_CONTRACT_TYPE.RENTAL) {
                          return name;
                        }
                        const refund = contract?.refund_percentage;
                        const refundText = refund !== null && refund !== undefined ? `${refund}%` : '';
                        return `${name} ${refundText}`;
                      })
                      .join(', ')}
                  />
                );
              })}
          </Contracts>
          {showAddMoreContractsButton && (
            <StyledButton onClick={addNewContracts} data-test-id='add-contract-type'>
              <AddIcon size={16} />
            </StyledButton>
          )}
        </>
      )}
    </Container>
  );
};

export default ShopDataModalContracts;
